import type { ReactNode } from "react";
import { Tabs as RadixTabs } from "radix-ui";
import { cx } from "./cx";

export interface TabItem<T extends string> {
  value: T;
  label: string;
  count?: number | string;
}

export function TabList<T extends string>({
  label,
  value,
  items,
  onChange,
  children,
  className,
}: {
  label: string;
  value: T;
  items: readonly TabItem<T>[];
  onChange: (value: T) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <RadixTabs.Root
      value={value}
      onValueChange={(v) => {
        const match = items.find((i) => i.value === v);
        if (match) onChange(match.value);
      }}
      activationMode="automatic"
      className={cx("flex min-h-0 flex-1 flex-col", className)}
    >
      <RadixTabs.List aria-label={label} className="mb-2.5 flex gap-5 border-b border-divider px-4 max-compact:px-2">
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.value}
            value={item.value}
            className="-mb-px inline-flex h-9 items-center gap-[7px] whitespace-nowrap border-b-2 border-transparent px-0.5 font-strong text-muted hover:text-fg data-[state=active]:border-link data-[state=active]:text-fg"
          >
            {item.label}
            {item.count === undefined ? null : (
              <span className="rounded-full bg-surface-2 px-1.5 font-mono text-xs leading-[17px] font-medium text-muted">
                {item.count}
              </span>
            )}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {children}
    </RadixTabs.Root>
  );
}

export function TabPanel({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return (
    <RadixTabs.Content
      value={value}
      className={cx("flex min-h-0 flex-1 flex-col focus-visible:outline-offset-[-2px]", className)}
    >
      {children}
    </RadixTabs.Content>
  );
}
